<template>
    <el-select
        v-model="model"
        class="sound-select"
        :placeholder="placeholder"
        :size="size"
        :class="selectClass"
        :filterable="filterable"
        @visible-change="dropdownVisibleChange"
    >
        <el-option
            v-for="option in options"
            v-bind="option"
            :key="option.RecordID"
            :value="option.RecordID"
            :label="option.Description"
        >
            <div
                v-if="option.Rec_Path"
                class="flex items-center flex-grow"
            >
                <el-tooltip
                    effect="dark"
                    :content="option.Description"
                    placement="top"
                    :disabled="!isTruncated"
                >
                    <span
                        v-ellipsis-check
                        class="sound-label"
                    >
                        {{ option.Description }}
                    </span>
                </el-tooltip>
                <SoundSelector
                    :ref="(el) => soundSelectorRefs[option.RecordID] = el"
                    class="sound-selector"
                    :parent-container-id="uid"
                    :sound-id="option.RecordID"
                    :src="option.Rec_Path"
                />
            </div>
            <div v-else>
                {{ option.Description }}
            </div>
        </el-option>
    </el-select>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Option as ElOption, Select as ElSelect } from 'element-ui'
import SoundSelector from '@/views/DashboardSettings/LayoutManagement/components/SoundSelector.vue'
import { UserRecord } from '@/types/entities'
import useGlobalUniqueID from '@/composables/useUniqueId'
import { getSoundObject, NONE_SOUND } from '@/composables/globalData/useEntitiesData'

/* Props */
const props = withDefaults(
    defineProps<{
        value?: string | number
        placeholder?: string
        size?: string
        filterable?: boolean
        selectClass?: string
        options?: Array<UserRecord>
    }>(),
    {
        value: undefined,
        placeholder: '',
        size: 'small',
        filterable: true,
        selectClass: '',
        options: () => []
    }
)

/* Emit */
const emit = defineEmits<{
    (event: 'input', value: string | number | undefined): void
}>()

/* Data */
const soundSelectorRefs = ref({})
const uid = useGlobalUniqueID()
const isTruncated = ref(false)

/* Computed */
const model = computed({
    get () {
        if (!props.value || props.value === '' || props.value === 'none') {
            return NONE_SOUND.RecordID
        }
        return props.value ? getSoundObject(props.options, props.value, NONE_SOUND).RecordID : undefined
    },
    set (value) {
        emit('input', value)
    }
})

/* Methods */
function dropdownVisibleChange (status?: UserRecord) {
    if (!status) {
        return
    }

    props.options.forEach(option => {
        const refEl = soundSelectorRefs.value[option.RecordID]

        if (refEl) {
            refEl.stop()
        }
    })
}
</script>

<style scoped>
.sound-label {
    @apply truncate;
    flex-grow: 1;
    max-width: 350px;
}

.sound-selector {
    @apply ml-3;
    min-width: 250px;
    width: auto;
}

[dir='rtl'] {
    .sound-selector {
        @apply mr-3;
        margin-left: unset;
    }
}
</style>
